import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_ACTIVE_PROJECTS } from '../graphql/queries';
import { Project } from '../types';
import { useAuth } from '../context/AuthContext';
import {
  LayoutGrid,
  Users,
  UserCog,
  Settings,
  Menu,
  X,
  Search,
  LogOut,
  Sparkles,
  CalendarRange,
  Sun,
  Moon,
  LayoutDashboard
} from 'lucide-react';


interface LayoutProps {
  children: React.ReactNode;
}

function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { data: activeProjectsData } = useQuery(GET_ACTIVE_PROJECTS);

  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };


  const userRole = user?.role ? user.role.toLowerCase() : '';

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/projects', label: 'Projects', icon: LayoutGrid },
    { path: '/roadmap', label: 'Roadmap', icon: CalendarRange },
    { path: '/clients', label: 'Clients', icon: Users, roles: ['admin', 'project_manager'] },
    { path: '/resources', label: 'Resources', icon: UserCog, roles: ['admin', 'project_manager'] },
  ].filter(item => !item.roles || item.roles.includes(userRole));

  const closeSidebar = () => setSidebarOpen(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getAvatarColor = (name?: string) => {
    const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444'];
    const index = name?.charCodeAt(0) ? name.charCodeAt(0) % colors.length : 0;
    return colors[index];
  };

  const getInitials = (name?: string) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  return (
    <div className="app-layout">
      {/* Mobile overlay */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={closeSidebar}
      />

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="workspace-info">
            <div className="workspace-avatar">
              <Sparkles size={16} />
            </div>
            <div className="workspace-details">
              <h2>PM Tools</h2>
              <span>by Sistematica</span>
            </div>
          </div>
        </div>

        <div className="sidebar-search" style={{ position: 'relative' }}>
          <Search size={14} style={{
            position: 'absolute',
            left: '26px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--sidebar-text)',
            opacity: 0.5,
            pointerEvents: 'none'
          }} />
          <input
            type="text"
            className="search-input"
            placeholder="Search..."
            style={{ paddingLeft: '34px' }}
            aria-label="Buscar"
          />
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}

          <div className="nav-section-title">Follow Up Projects</div>

          {activeProjectsData?.activeProjects?.filter((p: Project) => p.followUp).slice(0, 5).map((project: Project) => (
            <NavLink
              key={project.id}
              to={`/projects/${project.id}`}
              className="active-project-item"
              onClick={closeSidebar}
            >
              <span className="project-dot" style={{ backgroundColor: 'var(--warning)' }} />
              {project.name}
            </NavLink>
          ))}

          {userRole === 'admin' && (
            <NavLink
              to="/settings"
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={closeSidebar}
              style={{ marginTop: 'auto', marginBottom: '8px' }}
            >
              <Settings size={18} />
              Settings
            </NavLink>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar" style={{ background: getAvatarColor(user?.name) }}>
              {getInitials(user?.name)}
            </div>
            <div className="user-details">
              <h4>{user?.name || 'User'}</h4>
              <span>{user?.email || ''}</span>
            </div>
          </div>
          <button
            onClick={toggleTheme}
            className="btn-icon"
            style={{ 
              color: 'var(--sidebar-text)', 
              background: 'transparent', 
              border: 'none', 
              boxShadow: 'none',
              padding: '6px',
              marginLeft: '8px'
            }}
            title={theme === 'dark' ? 'Modo Claro' : 'Modo Oscuro'}
            aria-label={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            onClick={handleLogout}
            className="btn-icon"
            style={{ 
              color: 'var(--sidebar-text)', 
              background: 'transparent', 
              border: 'none', 
              boxShadow: 'none',
              padding: '6px',
              marginLeft: '8px'
            }}
            title="Logout"
            aria-label="Cerrar sesión"
          >
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {/* Mobile header */}
        <div className="mobile-header" style={{
          display: 'none',
          padding: '12px 16px',
          background: 'var(--surface)',
          borderBottom: '1px solid var(--border)',
          position: 'sticky',
          top: 0,
          zIndex: 50
        }}>
          <button
            className="mobile-menu-btn"
            onClick={() => setSidebarOpen(true)}
            style={{ display: 'flex' }}
            aria-label="Abrir menú de navegación"
          >
            <Menu size={24} />
          </button>
        </div>

        <style>{`
          @media (max-width: 1024px) {
            .mobile-header {
              display: flex !important;
            }
          }
        `}</style>

        {children}
      </main>
    </div>
  );
}

export default Layout;
